/**
 * Urenregistratie - Centraal overzicht en invoer
 */

import { useState, useEffect, useCallback } from 'react';
import { werkordersService, WeekOverzicht, ActieveWerkorder, WerkorderSectie, WerkorderTaak } from '../../services/werkorders.service';
import { werknemersService, Werknemer } from '../../services/werknemers.service';
import { useAuth } from '../../context/AuthContext';
import Toast from '../../components/Toast';
import './UrenRegistratie.css';

const UrenRegistratie = () => {
  const { user, isAdmin } = useAuth();
  const [werknemers, setWerknemers] = useState<Werknemer[]>([]);
  const [selectedWerknemer, setSelectedWerknemer] = useState<number | null>(null);
  const [weekStart, setWeekStart] = useState<Date>(getMonday(new Date()));
  const [weekOverzicht, setWeekOverzicht] = useState<WeekOverzicht | null>(null);
  const [actieveWerkorders, setActieveWerkorders] = useState<ActieveWerkorder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editRegistratie, setEditRegistratie] = useState<any>(null);

  // Secties en taken state
  const [secties, setSecties] = useState<WerkorderSectie[]>([]);
  const [selectedSectie, setSelectedSectie] = useState<number | null>(null);
  const [selectedTaak, setSelectedTaak] = useState<number | null>(null);
  const [takenVoorSectie, setTakenVoorSectie] = useState<WerkorderTaak[]>([]);


  // Form state
  const [formData, setFormData] = useState({
    werkorder_id: '',
    datum: '',
    starttijd: '08:00',
    eindtijd: '17:00',
    aantal_uren: 8,
    omschrijving: '',
    uurloon: 0,
  });

  // Get Monday of current week
  function getMonday(d: Date): Date {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  // Format date for API (lokale datum, niet UTC)
  const formatDate = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display (parse als lokale datum, niet UTC)
  const formatDisplayDate = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  };

  // Load werknemers
  useEffect(() => {
    const loadWerknemers = async () => {
      try {
        const data = await werknemersService.getAll({ actief: true });
        setWerknemers(data.items);
        
        // Altijd een werknemer selecteren als er nog geen geselecteerd is
        if (data.items.length > 0 && !selectedWerknemer) {
          let matchedWerknemer = null;
          
          // Probeer match te vinden met ingelogde gebruiker
          if (user?.username) {
            matchedWerknemer = data.items.find(w => 
              // Email match (Dylan, Wessel)
              (w.email && w.email.toLowerCase().trim() === user.username.toLowerCase().trim()) ||
              // Voornaam match (Matthijs)
              (w.voornaam && w.voornaam.toLowerCase().trim() === user.username.toLowerCase().trim())
            );
          }
          
          // Altijd een werknemer selecteren - match of eerste beschikbare
          const selectedId = matchedWerknemer ? matchedWerknemer.id : data.items[0].id;
          setSelectedWerknemer(selectedId);
        }
      } catch (err: any) {
        console.error('Fout bij laden werknemers:', err);
      }
    };
    loadWerknemers();
  }, [user]);

  // Load actieve werkorders
  useEffect(() => {
    const loadActieveWerkorders = async () => {
      try {
        const data = await werkordersService.getActieveWerkorders();
        setActieveWerkorders(data);
      } catch (err: any) {
        console.error('Fout bij laden werkorders:', err);
      }
    };
    loadActieveWerkorders();
  }, []);

  // Load week overzicht
  const loadWeekOverzicht = useCallback(async () => {
    if (!selectedWerknemer) return;

    try {
      setLoading(true);
      const data = await werkordersService.getWeekOverzicht(
        selectedWerknemer,
        formatDate(weekStart)
      );
      setWeekOverzicht(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon weekoverzicht niet laden');
      setWeekOverzicht(null);
    } finally {
      setLoading(false);
    }
  }, [selectedWerknemer, weekStart]);

  useEffect(() => {
    loadWeekOverzicht();
  }, [loadWeekOverzicht]);

  // Week navigatie
  const goToPreviousWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() - 7);
    setWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + 7);
    setWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    setWeekStart(getMonday(new Date()));
    // Open modal voor vandaag
    openNewModal(formatDate(new Date()));
  };

  // Open modal voor nieuwe registratie
  const openNewModal = (datum: string) => {
    const werknemer = werknemers.find(w => w.id === selectedWerknemer);
    setFormData({
      werkorder_id: '',
      datum: datum,
      starttijd: '08:00',
      eindtijd: '17:00',
      aantal_uren: 8,
      omschrijving: '',
      uurloon: werknemer?.bruto_uurloon || 0,
    });
    setSecties([]);
    setSelectedSectie(null);
    setSelectedTaak(null);
    setTakenVoorSectie([]);
    setEditRegistratie(null);
    setShowModal(true);
  };

  // Open modal voor bewerken
  const openEditModal = async (registratie: any, datum: string) => {
    const werknemer = werknemers.find(w => w.id === selectedWerknemer);
    setFormData({
      werkorder_id: registratie.werkorder_id.toString(),
      datum: datum,
      starttijd: registratie.starttijd ? new Date(registratie.starttijd).toTimeString().slice(0, 5) : '08:00',
      eindtijd: registratie.eindtijd ? new Date(registratie.eindtijd).toTimeString().slice(0, 5) : '17:00',
      aantal_uren: registratie.aantal_uren,
      omschrijving: registratie.omschrijving || '',
      uurloon: registratie.uurloon || werknemer?.bruto_uurloon || 0,
    });

    setEditRegistratie(registratie);
    setShowModal(true);

    // Laad secties voor deze werkorder
    try {
      const taken = await werkordersService.getWerkorderTaken(registratie.werkorder_id);
      setSecties(taken.secties);

      // Herstel sectie en taak selectie uit registratie
      if (registratie.sectie_id) {
        setSelectedSectie(registratie.sectie_id);
        const sectie = taken.secties.find((s: any) => s.id === registratie.sectie_id);
        if (sectie) {
          setTakenVoorSectie(sectie.taken || []);
          if (registratie.taak_id) {
            setSelectedTaak(registratie.taak_id);
          }
        }
      }
    } catch (err) {
      console.error('Fout bij laden secties:', err);
      setSecties([]);
    }
  };



  // Handle sectie selectie - update taken lijst
  const handleSectieChange = (sectieId: string) => {
    const id = sectieId ? parseInt(sectieId) : null;
    setSelectedSectie(id);
    setSelectedTaak(null);

    if (id) {
      const sectie = secties.find(s => s.id === id);
      setTakenVoorSectie(sectie?.taken || []);
    } else {
      setTakenVoorSectie([]);
    }
  };

  // Handle taak selectie - vul omschrijving in
  const handleTaakChange = (taakId: string) => {
    const id = taakId ? parseInt(taakId) : null;
    setSelectedTaak(id);

    if (id) {
      const taak = takenVoorSectie.find(t => t.id === id);
      if (taak) {
        setFormData({
          ...formData,
          omschrijving: taak.omschrijving
        });
      }
    }
  };

  // Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.werkorder_id || !selectedWerknemer) {
      setError('Selecteer een werkorder');
      return;
    }

    // Validatie: sectie en taak zijn verplicht als er secties beschikbaar zijn
    if (secties.length > 0 && !selectedSectie) {
      setError('Selecteer een sectie');
      return;
    }

    if (takenVoorSectie.length > 0 && !selectedTaak) {
      setError('Selecteer een taak');
      return;
    }

    try {
      const datum = new Date(formData.datum);
      const [startH, startM] = formData.starttijd.split(':').map(Number);
      const [eindH, eindM] = formData.eindtijd.split(':').map(Number);

      const starttijd = new Date(datum);
      starttijd.setHours(startH, startM, 0, 0);

      const eindtijd = new Date(datum);
      eindtijd.setHours(eindH, eindM, 0, 0);

      const registratieData = {
        werknemer_id: selectedWerknemer,
        datum: formData.datum,
        starttijd: starttijd.toISOString(),
        eindtijd: eindtijd.toISOString(),
        aantal_uren: formData.aantal_uren,
        omschrijving: formData.omschrijving || undefined,
        uurloon: formData.uurloon,
        totaal_kosten: formData.aantal_uren * formData.uurloon,
        goedgekeurd: false,
        sectie_id: selectedSectie || undefined,
        taak_id: selectedTaak || undefined,
      };

      if (editRegistratie) {
        await werkordersService.updateTijdregistratie(editRegistratie.id, registratieData);
        setSuccess('Urenregistratie bijgewerkt');
      } else {
        await werkordersService.createTijdregistratie(
          parseInt(formData.werkorder_id),
          registratieData as any
        );
        setSuccess('Uren geregistreerd');
      }

      setShowModal(false);
      loadWeekOverzicht();
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    }
  };

  // Delete registratie
  const handleDelete = async () => {
    if (!editRegistratie || !window.confirm('Weet je zeker dat je deze registratie wilt verwijderen?')) {
      return;
    }

    try {
      await werkordersService.deleteTijdregistratie(editRegistratie.id);
      setSuccess('Registratie verwijderd');
      setShowModal(false);
      loadWeekOverzicht();
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen');
    }
  };

  // Week label
  const getWeekLabel = (): string => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
    return `${weekStart.toLocaleDateString('nl-NL', options)} - ${weekEnd.toLocaleDateString('nl-NL', options)} ${weekEnd.getFullYear()}`;
  };

  return (
    <div className="urenregistratie-container">
      <div className="page-header">
        <h1>Urenregistratie</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => openNewModal(formatDate(new Date()))}>
            + Uren Registreren
          </button>
        </div>
      </div>

      <Toast message={error} type="error" onClose={() => setError(null)} autoClose={0} />
      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Werknemer</label>
          <select
            className="filter-select"
            value={selectedWerknemer || ''}
            onChange={(e) => setSelectedWerknemer(parseInt(e.target.value))}
          >
            <option value="">Selecteer werknemer</option>
            {werknemers.map(w => (
              <option key={w.id} value={w.id}>
                {w.voornaam} {w.tussenvoegsel ? w.tussenvoegsel + ' ' : ''}{w.achternaam}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Week navigatie */}
      <div className="week-navigation">
        <button onClick={goToPreviousWeek}>&larr; Vorige week</button>
        <button onClick={goToCurrentWeek}>Vandaag</button>
        <span className="week-label">{getWeekLabel()}</span>
        <span className="totaal-uren">
          Totaal: {weekOverzicht?.totaal_uren.toFixed(1) || 0} uur
        </span>
        <button onClick={goToNextWeek}>Volgende week &rarr;</button>
      </div>

      {loading ? (
        <div className="loading">Laden...</div>
      ) : weekOverzicht ? (
        <>
          {/* Week samenvatting */}
          <div className="week-summary">
            <div className="summary-card uren">
              <div className="label">Totaal uren</div>
              <div className="value">{weekOverzicht.totaal_uren.toFixed(1)}</div>
            </div>
            {isAdmin() && weekOverzicht.totaal_kosten !== null && (
              <div className="summary-card kosten">
                <div className="label">OMZET OP UREN</div>
                <div className="value">
                  {new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
                    weekOverzicht.totaal_kosten || 0
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Week grid */}
          <div className="week-grid">
            {weekOverzicht.dagen.map((dag, index) => (
              <div key={dag.datum} className={`dag-card ${index >= 5 ? 'weekend' : ''}`}>
                <div className={`dag-header ${index >= 5 ? 'weekend' : ''}`}>
                  <div>
                    <div className="dag-naam">{dag.dag_naam}</div>
                    <div className="dag-datum">{formatDisplayDate(dag.datum)}</div>
                  </div>
                  <div className="dag-totaal">{dag.totaal_uren.toFixed(1)}u</div>
                </div>
                <div className="dag-body">
                  {dag.registraties.map(reg => (
                    <div
                      key={reg.id}
                      className={`dag-registratie ${reg.goedgekeurd ? 'goedgekeurd' : ''}`}
                      onClick={() => openEditModal(reg, dag.datum)}
                    >
                      <div className="werkorder">{reg.klant}</div>
                      <div className="uren">
                        {reg.aantal_uren.toFixed(1)} uur{isAdmin() && reg.uurloon != null ? ` à €${reg.uurloon.toFixed(2)} = €${(reg.totaal_kosten || 0).toFixed(2)}` : ''}
                      </div>
                      {reg.omschrijving && (
                        <div className="omschrijving">{reg.omschrijving}</div>
                      )}
                    </div>
                  ))}
                  <button
                    className="add-uren-btn"
                    onClick={() => openNewModal(dag.datum)}
                  >
                    + Uren toevoegen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="loading">Selecteer een werknemer om het weekoverzicht te bekijken</div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editRegistratie ? 'Uren bewerken' : 'Uren registreren'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Totaal uren voor deze dag */}
                {weekOverzicht && formData.datum && (
                  <div style={{
                    background: '#e9ecef',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>Totaal deze dag:</span>
                    <strong style={{ color: '#28a745' }}>
                      {(weekOverzicht.dagen.find(d => d.datum === formData.datum)?.totaal_uren || 0).toFixed(1)} uur
                    </strong>
                  </div>
                )}
                <div className="form-group">
                  <label>Werkorder *</label>
                  <select
                    value={formData.werkorder_id}
                    onChange={async (e) => {
                      const werkorderId = e.target.value;
                      setFormData({ ...formData, werkorder_id: werkorderId });
                      setSelectedSectie(null);
                      setSelectedTaak(null);
                      setTakenVoorSectie([]);
                      
                      if (werkorderId) {
                        try {
                          const taken = await werkordersService.getWerkorderTaken(parseInt(werkorderId));
                          setSecties(taken.secties);
                        } catch (err) {
                          console.error('Fout bij laden secties:', err);
                          setSecties([]);
                        }
                      } else {
                        setSecties([]);
                      }
                    }}
                    required
                  >
                    <option value="">Selecteer werkorder</option>
                    {actieveWerkorders.map(wo => (
                      <option key={wo.id} value={wo.id}>
                        {wo.werkordernummer} - {wo.klant} - {wo.onderwerp}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sectie dropdown - alleen tonen als er secties zijn */}
                {secties.length > 0 && (
                  <div className="form-group">
                    <label>Sectie *</label>
                    <select
                      value={selectedSectie || ''}
                      onChange={(e) => handleSectieChange(e.target.value)}
                      required
                    >
                      <option value="">Selecteer sectie</option>
                      {secties.map(sectie => (
                        <option key={sectie.id} value={sectie.id}>
                          {sectie.naam}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Taak dropdown - alleen tonen als er taken zijn voor de geselecteerde sectie */}
                {takenVoorSectie.length > 0 && (
                  <div className="form-group">
                    <label>Taak *</label>
                    <select
                      value={selectedTaak || ''}
                      onChange={(e) => handleTaakChange(e.target.value)}
                      required
                    >
                      <option value="">Selecteer taak</option>
                      {takenVoorSectie.map(taak => (
                        <option key={taak.id} value={taak.id}>
                          {taak.omschrijving} ({taak.aantal} {taak.eenheid})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={formData.datum}
                    onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Aantal uren</label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    max="24"
                    value={formData.aantal_uren === 0 ? '' : formData.aantal_uren}
                    onChange={(e) => setFormData({ ...formData, aantal_uren: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                    onBlur={(e) => {
                      if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                        setFormData({ ...formData, aantal_uren: 0 });
                      }
                    }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Omschrijving werkzaamheden</label>
                  <textarea
                    value={formData.omschrijving}
                    onChange={(e) => setFormData({ ...formData, omschrijving: e.target.value })}
                    placeholder="Wat heb je gedaan?"
                  />
                </div>
              </div>

              <div className="modal-footer">
                {editRegistratie && (
                  <button type="button" className="btn btn-danger" onClick={handleDelete}>
                    Verwijderen
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Annuleren
                </button>
                <button type="submit" className="btn btn-primary">
                  {editRegistratie ? 'Opslaan' : 'Registreren'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UrenRegistratie;
